import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { HttpService } from 'app/services';
import { locale as english } from '../i18n/en';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { MediaService } from 'app/services/media.service';

type ImgLink = {
  id: string;
  link: string;
  timestamp: string;
  key: string;
};

interface ViewerImg {
  key: string;
  url: string,
  dateStr: string,
  timeStr: string,
  timestamp: string,
  thumbnailUrl: string;
  filename: string;
}

export interface Orientation {
  PORTRAIT: 'Portrait',
  LANDSCAPE: 'Landscpe'
}
const dtFormatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute:'numeric',
        second:'numeric'
      }),
      formatDatetime = ts => dtFormatter.format(new Date(ts));

@Component({
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss']
})
export class ImageViewerComponent {
  deviceId;
  deviceSerial;
  images: ViewerImg[] = [];
  isFullscreen = false;
  selectedImg;
  selectedIndex;

  batchSize = 200;
  imageActivity;
  loadingprev: boolean = false;
  loadingnext: boolean = true;
  hopped: boolean = false
  prevExhausted: boolean = false;
  nextExhausted: boolean = false;
  landscape: boolean;
  observations = [];
  observationsLoading = true;
  constructor(
    private dialogRef: MatDialogRef<ImageViewerComponent>,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private httpService: HttpService,
    public mediaService: MediaService
  ) {

    this.landscape = this.mediaService.currentOrientation == 'landscape';
    this.mediaService.orientation.subscribe(res => {
       this.landscape = (res == 'landscape')
    });

    this._fuseTranslationLoaderService.loadTranslations(english);
    let hFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    this.deviceId = data.device.id;
    this.deviceSerial = data.device.device_serial;
    this.httpService.getDeviceImageActivity(this.deviceId, Intl.DateTimeFormat().resolvedOptions().timeZone).then(res => {
      this.imageActivity = res;
      this.imageActivity.forEach(el => {
        el.label = `${hFormatter.format(new Date(el.date))} (${el.count} images)`;
      });
    });
    setTimeout(() => (document.activeElement as any)?.blur(), 250);
    this.getBatchInSequence('next').then(() => {
      //this.initLoading = false;
      this.loadingnext = false;
      setTimeout(() => (document.querySelector('.arrow.right') as any)?.focus(), 500); //hacky, but preserves keyboard controls
      if(this.images.length == 0){
        this.nextExhausted = true;
        this.prevExhausted = true;
      }
    });
  }

  async hopToDate(e){
    let dateStr = e.target.innerText.replace(/^([A-z]+ \d+, \d{4}).*/, '$1');
    let dateHeader = Array.from(document.querySelectorAll('.date-header')).find((el: any) => {
      return el.innerText == dateStr;
    });
    if(dateHeader){
      let firstImg = dateHeader.nextElementSibling;
      let container = document.querySelector('.thumbnails');
      container?.scrollBy({behavior: 'smooth', left: 0, top: firstImg.getBoundingClientRect().top - 100});
      return ;
    } else {
      this.prevExhausted = true;
      this.nextExhausted = true;
      this.hopped = true;
      this.loadingnext = true;
      this.images = [];
      let date = new Date(new Date(dateStr).getTime() + 86400000);
      let [yyyy, MM, dd, HH, mm, ss] = [
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        '00',
        '00',
        '00'
      ];
      if(MM < 10)
        MM = `0${MM}` as any;
      if(dd < 10)
        dd = `0${dd}` as any;
      
      let dateKey = `${yyyy}/${MM}/${dd}/${this.deviceSerial}/${this.deviceSerial}_${yyyy}${MM}${dd}${HH}${mm}${ss}.jpg`;
      this.getImages(null, dateKey, 'DESC', this.batchSize).then(res => {
        this.prevExhausted = false;
        this.nextExhausted = false;
        this.loadingnext = false;
        this.images = res;
      });
    }
  }

  async getImages(from?: string, to?: string, order?: string, count?: number){
    if(from)
      from = `${encodeURIComponent(from)}`;
    if(to)
      to = `${encodeURIComponent(to)}`;
    let imgs = await this.httpService.getDeviceImagesDirect(this.deviceId, from, to, order, count)
    .then((imgLinks: ImgLink[]) => {
      return imgLinks.map((il: ImgLink): ViewerImg => {
        let [dateStr, timeStr] = formatDatetime(il.timestamp).split(' at ');
        return {
          filename: il.key.replace(/.*\/([A-z0-9_]+\.jpg)/, '$1'),
          timestamp: il.timestamp,
          key: il.key,
          dateStr: dateStr,
          timeStr: timeStr,
          url: il.link,
          thumbnailUrl: il.link
        };
      });
    });
    return imgs;
  }

  rowAdded = false;
  
  async getBatchInSequence(dir: string){
    if(!this.hopped)
      if(this[`${dir}Exhausted`])
        return console.log(`${dir} content has been exhausted, terminating`);
    this[`loading${dir}`] = true;

    let from, to, next, count, order;
    if(dir == 'next'){
      order = 'DESC';
      if(this.images.length)
        to = this.images[this.images.length - 1]?.key;
    } else {
      order = 'ASC';
      from = this.images[0]?.key;
    }
    count = this.batchSize;

    let thumbnailContainer = document.querySelector('.thumbnails'),
        initScrollTop = thumbnailContainer?.scrollTop,
        initScrollHeight = thumbnailContainer?.scrollHeight;

    await this.getImages(from, to, order, count).then(imgs => {
      if(!from && !to){
        if(this.hopped){
          this.hopped = false; //reset hop flag
        } else {
          this.prevExhausted = true; //initial load; batch already contains most recent
        }
      } else {
        if(!imgs.length || imgs.length < this.batchSize){
          this[`${dir}Exhausted`] = true;
        }
      }

      if(thumbnailContainer){
        let obs = new MutationObserver(() => {
          let renderedThumbnails = thumbnailContainer?.querySelectorAll('.thumbnail');
          if(renderedThumbnails.length === this.images.length){
            this[`loading${dir}`] = false;
            obs.disconnect();
          }
        });
        obs.observe(thumbnailContainer, {childList: true, subtree: false, attributes: false});
      }

      if(dir == 'prev'){
        this.images = [...imgs.reverse(), ...this.images];
        this.selectedIndex += imgs.length;
      } else {
        this.images = [...this.images, ...imgs];
      }


      if(this.selectedIndex == null){
        this.selectedIndex = 0;
        this.selectedImg = this.images[0];
        this.getObservations();
      }
    });
  }
  insectType: string;
  insectCount: number;
  insectNotes: string;
  isInfoOpen = false;

  toggleInfo(){
    document.querySelector('.img-info')?.classList.toggle('open')
  }

  addRow(){
    this.httpService.postObservation(this.deviceId, this.images[this.selectedIndex].key).then(res => {
      this.observations.push(res);
    });
  }

  updateRow(event, observation, field){
    let propName, value;
    switch(field){
      case 'insectname':
        propName = 'insect_type';
        value = event.target?.value;
        break;
      case 'insectcount':
        propName = 'insect_count';
        value = parseInt(event.target?.value) || 0;
        break;
      case 'boardtype':
        propName = 'board';
        value = event.target?.value;
        break;
      case 'pheremones':
        propName = 'pheremones';
        value = event.target?.value;
        break;
      case 'insectnotes':
        propName = 'notes';
        value = event.target?.value;
        break;
    }
    observation[propName] = value;
    this.httpService.updateObservation({id: observation.id, [propName]: value});
  }
  
  title(img: ImgLink) {
    return new Date(img.timestamp).toLocaleString().replace(',', '');
  }

  selectIndex(i) {
    this.observationsLoading = true;
    (document.querySelector('.arrow.right') as any)?.focus(); //hacky, but preserves keyboard controls
    let container = document.querySelector('.thumbnails');
    let scrollY;

    if(i >= 0 && i < this.images.length){
      this.selectedIndex = i;
      this.selectedImg = this.images[i];
      let header = document.querySelector('.thumbnails h2');
      let selected = document.querySelector(`.thumbnail:has(img[src="${this.images[i].thumbnailUrl}"])`)
      let containerBounds = container.getBoundingClientRect();
      let headerBounds = header.getBoundingClientRect();
      let selectedBounds = selected.getBoundingClientRect();

      if(selectedBounds.top < (containerBounds.top + headerBounds.height)){
        scrollY = selectedBounds.top - (containerBounds.top + headerBounds.height) - 5;
      } else if(selectedBounds.bottom > containerBounds.bottom){
        scrollY = selectedBounds.bottom - containerBounds.bottom + 5;
      }
    } else {
      scrollY = i < 0 ? 0 : this.getMaxScroll(container);
    }
    this.getObservations();

    if(scrollY == null){
      return;
    }

    container.scrollBy({behavior: 'smooth', left: 0, top: scrollY});
  }

  getObservations(){
    this.httpService.getImgObservations(this.deviceId, this.images[this.selectedIndex].key).then((res: any) => {
      if(res && res.length > 0) {
        this.observations = res;
      } else
        this.observations = [];
      this.observationsLoading = false;
    })
  }

  deleteRow(id){
    this.httpService.deleteObservation(id);
    this.observations.splice(this.observations.findIndex(el => el.id == id), 1);
  }

  toggleFullscreen(){
    this.isFullscreen = !this.isFullscreen;
    (document.querySelector('.arrow.right') as any)?.focus(); //hacky, but preserves keyboard controls
  }

  close() {
    this.dialogRef.close();
  }

  async downloadImage(img: ViewerImg, event?: MouseEvent) {
    if (event) event.stopPropagation();

    try {
      const link = document.createElement('a');
      link.href = img.url;
      link.download = `${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  }

  keyhandler(e){
    if(document.activeElement.tagName == 'INPUT')
      return;
    switch(e.key){
      case 'ArrowRight':
        this.selectIndex(this.selectedIndex + 1);
        break;
      case 'ArrowLeft':
        this.selectIndex(this.selectedIndex - 1);
        break;
      default: return;
    }
    e.preventDefault();
    e.stopPropagation();
  }

  getMaxScroll(el){
    return el.scrollHeight - el.clientHeight - 1;
  }

  scrollhandler(e){
    if(e.target.scrollTop == 0 && this.loadingprev == false){
      this.loadingprev = true;
      this.getBatchInSequence('prev');
    }
    if(e.target.scrollTop >= this.getMaxScroll(e.target) && this.loadingnext == false){
        this.loadingnext = true;
        this.getBatchInSequence('next');
    }
  }
}

